import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { joinMerkmalCsvIds, parseMerkmalCsvIds } from '../../utils/contentMasterWawiExtendedFields';

export type ContentMasterMerkmalMultiOption = {
  kMerkmalWert: number;
  label?: string;
};

type ContentMasterMerkmalMultiEditorProps = {
  options: ContentMasterMerkmalMultiOption[];
  value: string;
  onChange: (csv: string) => void;
  disabled?: boolean;
};

export function ContentMasterMerkmalMultiEditor({
  options,
  value,
  onChange,
  disabled = false,
}: ContentMasterMerkmalMultiEditorProps) {
  const { t } = useTranslation();
  const [filterQuery, setFilterQuery] = useState('');
  const selectedSet = useMemo(() => new Set(parseMerkmalCsvIds(value)), [value]);
  const normalizedQuery = filterQuery.trim().toLowerCase();

  const updateSelection = (nextSet: Set<string>) => {
    onChange(joinMerkmalCsvIds(Array.from(nextSet)));
  };

  const toggleOption = (idStr: string, checked: boolean) => {
    const next = new Set(selectedSet);
    if (checked) {
      next.add(idStr);
    } else {
      next.delete(idStr);
    }
    updateSelection(next);
  };

  const clearAll = () => {
    if (selectedSet.size === 0) {
      return;
    }
    updateSelection(new Set());
  };

  if (!options.length) {
    return (
      <div className="content-master-merkmal-multi">
        <div
          className="content-master-merkmal-multi__empty"
          title={t('content_master_merkmal_no_options')}
        >
          {t('content_master_merkmal_no_options')}
        </div>
      </div>
    );
  }

  return (
    <div className="content-master-merkmal-multi">
      <div className="content-master-merkmal-multi__toolbar">
        <input
          type="search"
          className="content-master-merkmal-multi__search"
          value={filterQuery}
          onChange={(event) => setFilterQuery(event.target.value)}
          placeholder={t('content_master_merkmal_search_placeholder')}
          disabled={disabled}
          aria-label={t('content_master_merkmal_search_placeholder')}
        />
        <button
          type="button"
          className="content-master-merkmal-multi__clear"
          onClick={clearAll}
          disabled={disabled || selectedSet.size === 0}
        >
          {t('content_master_merkmal_clear_all')}
        </button>
      </div>
      <div
        className="content-master-merkmal-multi__list"
        role="group"
        aria-label={t('content_master_merkmal_options_label')}
      >
        {options.map((opt) => {
          const idStr = String(opt.kMerkmalWert);
          const label = opt.label ?? idStr;
          const checked = selectedSet.has(idStr);
          const matchesFilter =
            normalizedQuery.length === 0 || label.toLowerCase().includes(normalizedQuery);
          return (
            <label
              key={idStr}
              className={`content-master-merkmal-multi__option${
                disabled ? ' content-master-merkmal-multi__option--disabled' : ''
              }${matchesFilter ? '' : ' content-master-merkmal-multi__option--hidden'}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={(event) => toggleOption(idStr, event.target.checked)}
                disabled={disabled}
              />
              <span>{label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
